import React from 'react';
import { Link } from 'react-router-dom';
import './HowItWorks.css';

const steps = [
  {
    n: '01', icon: '🏅', title: 'Teams & Events Register',
    desc: "Any youth sports team or tournament organizer can register for free on our website. Fill in your name, sport, location, and upload a logo. Once approved you receive a unique QR code.",
    detail: ['Fill out the simple registration form', 'Choose Team or Event registration', 'Upload your logo', 'Get approved and receive your QR code ready to print']
  },
  {
    n: '02', icon: '⚔️', title: 'Compete & Attend',
    desc: "Players bring their phones to games, tournaments, and showcases. After competing against a team or checking in to an event, it's time to scan.",
    detail: ['Works at any sanctioned tournament or showcase', 'Scan team patches after competing', 'Check in to events with a single scan', 'Works on iOS and Android']
  },
  {
    n: '03', icon: '📱', title: 'Scan the QR Code',
    desc: "Open the TPG app and tap the gold scan button. Point your camera at the team or event QR code. For teams, enter the date and optionally record the score.",
    detail: ['Instant QR recognition for teams and events', 'Add date and optional score for team scans', 'Event check-ins save instantly', 'Supports multiple kids per account']
  },
  {
    n: '04', icon: '📍', title: 'They Appear on Your Map',
    desc: "Teams and events are instantly pinned on your personal map with their logo. Gold pins for teams, purple pins for events — watch your map fill up as you travel the country.",
    detail: ["Gold pins = teams you've played", 'Purple pins = events you have attended', 'View game history, W/L record, and scores', 'Sort and filter by sport']
  },
  {
    n: '05', icon: '🏆', title: 'Earn Trophies',
    desc: "Hit milestones to unlock trophies. Collect enough patches, play teams in enough states, and watch the confetti fly when you earn a new achievement.",
    detail: ['6 patch count trophies (1 to 100)', '6 state milestone trophies (5 to all 50)', 'Confetti popup on every unlock', 'Trophy case to show off your progress']
  },
];

export default function HowItWorks() {
  return (
    <div className="hiw">
      {/* Hero */}
      <section className="hiw__hero">
        <div className="container hiw__hero-inner">
          <div className="badge badge-blue" style={{marginBottom:'20px'}}>Step by Step</div>
          <h1 className="section-title animate-fade-up">How The Patch Guys Works</h1>
          <p className="section-subtitle animate-fade-up delay-1" style={{textAlign:'center', margin:'0 auto 40px'}}>
            From registering your team or event to earning your first trophy — here's everything you need to know about The Patch Guys.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="section hiw__steps">
        <div className="container">
          {steps.map((s, i) => (
            <div key={i} className={`hiw-step ${i % 2 === 1 ? 'hiw-step--reverse' : ''}`}>
              <div className="hiw-step__visual">
                <div className="hiw-step__number-bg">{s.n}</div>
                <div className="hiw-step__icon-wrap">
                  <span className="hiw-step__icon">{s.icon}</span>
                </div>
              </div>
              <div className="hiw-step__content">
                <div className="hiw-step__num-label">{s.n}</div>
                <h2 className="hiw-step__title">{s.title}</h2>
                <p className="hiw-step__desc">{s.desc}</p>
                <ul className="hiw-step__details">
                  {s.detail.map((d, j) => (
                    <li key={j}>
                      <span className="hiw-step__check">✓</span>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="section hiw__faq" style={{background:'var(--off-white)'}}>
        <div className="container">
          <div style={{textAlign:'center', marginBottom:'56px'}}>
            <div className="tag-line">Got Questions?</div>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>
          <div className="hiw__faq-grid">
            {[
              { q: 'Is the app free to download?', a: 'Yes! The TPG app is completely free to download and use on both iOS and Android.' },
              { q: 'How do teams and events get their QR code?', a: 'Teams and event organizers register through our website. Once approved by the TPG team, they receive a unique QR code to print and display at games and events.' },
              { q: 'What is the difference between a team and an event?', a: 'A team is a sports club or traveling team that players compete against at games. An event is a tournament or showcase that players attend. Both get their own QR code — teams are shown as gold pins on the map, events as purple pins.' },
              { q: 'Can I have multiple kids on one account?', a: 'Absolutely. One account can have multiple child profiles, each with their own independent patch map, game history, and trophy progress.' },
              { q: 'Do I need internet to scan a patch?', a: 'You need a connection to save the scan to your account. The app will alert you if something goes wrong and let you try again.' },
              { q: 'What sports are supported?', a: 'Baseball, Basketball, Field Hockey, Football, Ice Hockey, Soccer, Softball, and Volleyball. More sports coming soon!' },
              { q: 'How does the approval process work?', a: 'After a team or event submits their registration, the TPG team reviews it (usually within 1-2 business days) and approves it. Once approved, the QR code becomes scannable.' },
            ].map((item, i) => (
              <div key={i} className="hiw-faq-item">
                <h3 className="hiw-faq-item__q">{item.q}</h3>
                <p className="hiw-faq-item__a">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section hiw__cta">
        <div className="container" style={{textAlign:'center'}}>
          <h2 className="section-title" style={{marginBottom:'16px'}}>Ready to Start?</h2>
          <p className="section-subtitle" style={{margin:'0 auto 40px', textAlign:'center'}}>
            Download the app and scan your first patch today.
          </p>
          <div style={{display:'flex', gap:'16px', justifyContent:'center', flexWrap:'wrap'}}>
            <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
              Download the App
            </a>
            <Link to="/register" className="btn btn-secondary btn-lg">
              Register Team or Event
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}